import React, { useContext, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
// @ts-expect-error - isUserManaged exists at runtime; package types are outdated
import { getCommunityLoginUrl, isUserManaged } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';
import ChampionApplicationForm from '../components/champions/ChampionApplicationForm';

const TITLE = 'Champions Signup | Atlassian Community';
const DESCRIPTION = 'Apply to become an Atlassian Champion.';
const CHAMPIONS_SIGNUP_PATH = '/champions/signup';

const getSignupRedirectUrl = () => `${process.env.HOME_BASE_URL}${CHAMPIONS_SIGNUP_PATH}`;

const UNMANAGED_BANNER_MESSAGE =
  'Please use your organization-managed Atlassian account to apply for the Champions program.';

export const ChampionsSignupPage: React.FC = () => {
  const { user, userIsInitialized } = useContext(UserContext) ?? {};
  const isLoggedIn = !!user;
  const isAuthSignupEnabled = championsAuthSignupIsActive();
  const [accountIsManaged, setAccountIsManaged] = useState<boolean | null>(null);
  const homepage =
    process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  useEffect(() => {
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }
    if (!userIsInitialized || !isLoggedIn) {
      window.location.href = getCommunityLoginUrl(
        { redirectUrl: getSignupRedirectUrl(), experience: 'entry-auth' },
        getEnvironmentAsEnum(),
      );
      return;
    }
    if (!user?.account_id) {
      setAccountIsManaged(null);
      return;
    }
    let cancelled = false;
    isUserManaged(user.account_id, homepage, homepage)
      .then((result: boolean) => !cancelled && setAccountIsManaged(result))
      .catch(() => !cancelled && setAccountIsManaged(null));
    return () => {
      cancelled = true;
    };
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, user?.account_id, homepage]);

  const canShowContent = isAuthSignupEnabled && userIsInitialized && isLoggedIn;
  const showForm = canShowContent && accountIsManaged === true;

  if (!canShowContent) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithAnalytics eventScreenName="championsSignupScreen">
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div style={{ width: '100%' }}>
          {!showForm && <div style={unmanagedBannerStyle}>{UNMANAGED_BANNER_MESSAGE}</div>}
          {showForm && <ChampionApplicationForm />}
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsSignupPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.800'),
  paddingBottom: token('space.800'),
  minHeight: '400px',
});

const unmanagedBannerStyle: React.CSSProperties = {
  marginBottom: '16px',
  padding: '12px 16px',
  borderRadius: '3px',
  backgroundColor: '#FFEDEB',
  color: '#172B4D',
};
